import React, { useState, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import TimePicker from "@components/ui/selection/TimePicker";
import DropDown from "@components/ui/dropdown/DropDown";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDispoAtelierStore, ZONE_TYPES, ZONE_COLORS } from "@store/dispoAtelierStore";
import type { AvailabilityBlock, ZoneType } from "@store/disponibiliteStore";
import { getCachedWorkers } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { hourToTimeStr, timeStrToHour, snapToGrid } from "@utils/timeGrid";

const SNAP_QUARTER = 0.25;

const ZONE_OPTIONS = ZONE_TYPES.map((z) => ({
  value: z,
  label: ZONE_COLORS[z].label,
}));

interface BlockModalProps {
  mode: "edit" | "create";
  block?: AvailabilityBlock;
  workerId?: string;
  selectedWorkerIds?: string[];
  initialDate?: string;
  initialStartHour?: number;
  initialDuration?: number;
  onClose: () => void;
}

export function BlockModal({
  mode,
  block,
  workerId,
  selectedWorkerIds,
  initialDate,
  initialStartHour,
  initialDuration = SNAP_QUARTER,
  onClose,
}: BlockModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const HOURS_START = orgData?.config?.booking_opening_hour ?? 8;
  const HOURS_END = Math.max((orgData?.config?.booking_closing_hour ?? 18) + 2, 20);
  const effectiveStartHour = initialStartHour ?? HOURS_START;
  const updateBlock = useDispoAtelierStore((s) => s.updateBlock);
  const removeBlock = useDispoAtelierStore((s) => s.removeBlock);
  const addBlock = useDispoAtelierStore((s) => s.addBlock);

  const isEdit = mode === "edit" && block;

  const [label, setLabel] = useState(isEdit ? block.label : "");
  const [zoneType, setZoneType] = useState<ZoneType>(isEdit ? (block.zoneType || "mecanique") : "mecanique");
  const [recurring, setRecurring] = useState(isEdit ? block.recurring : false);
  const [startTime, setStartTime] = useState(
    hourToTimeStr(isEdit ? block.startHour : effectiveStartHour),
  );
  const [endTime, setEndTime] = useState(
    hourToTimeStr(
      isEdit
        ? block.startHour + block.durationHours
        : effectiveStartHour + initialDuration,
    ),
  );

  const [chosenWorkerId, setChosenWorkerId] = useState(
    isEdit ? block.workerId : (workerId || selectedWorkerIds?.[0] || ""),
  );

  const workerOptions = useMemo(() => {
    const workers = getCachedWorkers();
    if (isEdit) {
      return workers.map((w) => ({ value: w.id, label: w.name }));
    }
    const ids = selectedWorkerIds || (workerId ? [workerId] : []);
    return workers
      .filter((w) => ids.includes(w.id))
      .map((w) => ({ value: w.id, label: w.name }));
  }, [isEdit, selectedWorkerIds, workerId]);

  const showWorkerPicker = workerOptions.length > 1;

  const date = isEdit ? block.date : initialDate!;

  const dayOfWeek = useMemo(() => {
    const d = new Date(date + "T00:00:00");
    const day = d.getDay();
    return day === 0 ? 6 : day - 1;
  }, [date]);

  const handleSave = () => {
    const start = snapToGrid(timeStrToHour(startTime), SNAP_QUARTER);
    const end = snapToGrid(timeStrToHour(endTime), SNAP_QUARTER);
    const duration = end - start;
    if (duration <= 0) return;

    if (isEdit) {
      updateBlock(block.id, {
        workerId: chosenWorkerId,
        label: label.trim(),
        startHour: start,
        durationHours: duration,
        recurring,
        dayOfWeek: recurring ? dayOfWeek : undefined,
        zoneType,
      });
    } else {
      addBlock({
        workerId: chosenWorkerId,
        label: label.trim(),
        type: zoneType === "conge" ? "conge" : "off",
        date: initialDate!,
        startHour: start,
        durationHours: duration,
        recurring,
        dayOfWeek: recurring ? dayOfWeek : undefined,
        zoneType,
      }, orgData?.id || "");
    }
    onClose();
  };

  const handleDelete = () => {
    if (isEdit) {
      removeBlock(block.id);
      onClose();
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      title={isEdit ? "Modifier le créneau" : "Ajouter un créneau"}
      startIcon={<Icon1 name={isEdit ? "sett-dispo" : "plus"} size={20} />}
      width="max-w-600"
    >
      <div className="flex flex-col p-5" style={{ gap: 16 }}>
        {showWorkerPicker && (
          <DropDown
            label="Mécanicien"
            options={workerOptions}
            value={chosenWorkerId}
            onSelect={(v) => setChosenWorkerId(String(v))}
          />
        )}

        <DropDown
          label="Type de zone"
          options={ZONE_OPTIONS}
          value={zoneType}
          onSelect={(v) => setZoneType(v as ZoneType)}
        />

        <TextField
          label="Commentaires"
          value={label}
          onChange={(v) => setLabel(v)}
          placeholder="Ex: Mécanique - Lundi matin"
        />

        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <TimePicker
              label="Début"
              value={startTime}
              onChange={(v) => setStartTime(v)}
              minHour={HOURS_START}
              maxHour={HOURS_END}
              minuteStep={15}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Fin"
              value={endTime}
              onChange={(v) => setEndTime(v)}
              minHour={HOURS_START}
              maxHour={HOURS_END}
              minuteStep={15}
            />
          </div>
        </div>

        <SwitchBrick
          checked={recurring}
          onChange={(v) => setRecurring(v)}
          title="Récurrent"
          description="Se répète chaque semaine"
        />

        <div
          className={`flex flex-row gap-2 pt-2 ${isEdit ? "justify-between" : "justify-end"}`}
        >
          {isEdit && (
            <SecondaryButton
              label="Supprimer"
              danger
              onClick={handleDelete}
              variant="small"
            />
          )}
          <div className="flex flex-row gap-2">
            <SecondaryButton
              label="Annuler"
              onClick={onClose}
              variant="small"
            />
            <Button
              label={isEdit ? "Enregistrer" : "Ajouter"}
              onClick={handleSave}
              variant="small"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}
